import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  MenuItem,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@material-ui/core'
import { useNotify } from '@globaleris/ui.library'
import Quill, { RangeStatic } from 'quill'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom'
import {
  createPost,
  getImages,
  getPost,
  publishPost,
  replacePost
} from '../../../api/Argentia/endpoints/atlasPosts/atlasPosts.api'
import {
  AtlasLanguage,
  AtlasPostImage,
  AtlasPostStatus,
  AtlasPostWrite
} from '../../../api/Argentia/endpoints/atlasPosts/model/atlasPosts'
import Page, { PageLayout } from '../../../components/page/page'
import PageCenterColumn from '../../../components/page/pageCenterColumn'
import PageHeader from '../../../components/page/pageHeader'
import RichTextEditor from '../../../components/richTextEditor/richTextEditor'
import { BlogEditorView } from '../../../components/richTextEditor/richTextEditor.model'
import { StandardCard } from '../../../components/shared/standardCard/standardCard'
import { NonAuthRoutes } from '../../../model/enums/nonAuthRoutes'
import { AtlasBlogImages } from './atlasBlogImages'
import { cleanBlogHtml, imageUrlsInHtml, mapImageSources } from './atlasBlogHtml'
import {
  blogMarkdownImage,
  blogMarkdownLink,
  htmlToBlogMarkdown,
  markdownToBlogHtml,
  normalizeBlogLink
} from './atlasBlogMarkdown'

interface FormState {
  titulo: string
  idioma: AtlasLanguage
  resumen: string
  autor: string
  urlImagen: string
}

type LinkSelection =
  | { view: 'rich'; index: number; length: number }
  | { view: 'markdown'; start: number; end: number; label: string }

const blankForm: FormState = { titulo: '', idioma: 'ES', resumen: '', autor: '', urlImagen: '' }
type PublicationMode = 'now' | 'scheduled'

const localDateTime = (date: Date): string =>
  new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16)

// The image API is paged. Load until all images referenced by this article have fresh signed previews.
const findPreviews = async (urls: string[]): Promise<AtlasPostImage[]> => {
  const remaining = new Set(urls)
  const found: AtlasPostImage[] = []
  if (!remaining.size) return found
  let page = 1
  let total = 1
  while (page <= total && remaining.size) {
    const result = await getImages(page, 100)
    total = Math.ceil(result.totalCount / 100)
    result.items.forEach(image => {
      if (remaining.has(image.imageRef)) {
        found.push(image)
        remaining.delete(image.imageRef)
      }
    })
    page += 1
  }
  return found
}

export const AtlasBlogEditorPage: React.FC = () => {
  const { id } = useParams<{ id?: string }>()
  const isNew = !id
  const history = useHistory()
  const [t] = useTranslation(['atlasBlog'])
  const { error, info } = useNotify()
  const [form, setForm] = useState<FormState>(blankForm)
  const [initialHtml, setInitialHtml] = useState('')
  const [ready, setReady] = useState(isNew)
  const [loading, setLoading] = useState(!isNew)
  const [saving, setSaving] = useState(false)
  const [postStatus, setPostStatus] = useState<AtlasPostStatus | null>(null)
  const [publishedAt, setPublishedAt] = useState<string | null>(null)
  const [publicationMode, setPublicationMode] = useState<PublicationMode>('now')
  const [scheduledAt, setScheduledAt] = useState('')
  const [publishConfirmation, setPublishConfirmation] = useState(false)
  const [previewByStable, setPreviewByStable] = useState<Record<string, string>>({})
  const [blogView, setBlogView] = useState<BlogEditorView>('rich')
  const [markdownText, setMarkdownText] = useState('')
  const [markdownChanged, setMarkdownChanged] = useState(false)
  const [linkSelection, setLinkSelection] = useState<LinkSelection | null>(null)
  const [linkUrl, setLinkUrl] = useState('')
  const [linkError, setLinkError] = useState(false)
  const previewToStable = useRef<Record<string, string>>({})
  const quillRef = useRef<Quill | null>(null)
  const richSelectionRef = useRef<RangeStatic | null>(null)
  const markdownInputRef = useRef<HTMLTextAreaElement | null>(null)
  const pendingMarkdownCursor = useRef<number | null>(null)

  useEffect(() => {
    if (blogView !== 'markdown' || pendingMarkdownCursor.current === null || !markdownInputRef.current) return
    const cursor = pendingMarkdownCursor.current
    pendingMarkdownCursor.current = null
    markdownInputRef.current.focus()
    markdownInputRef.current.setSelectionRange(cursor, cursor)
  }, [blogView, markdownText])

  const rememberImages = (images: AtlasPostImage[]) => {
    const next: Record<string, string> = {}
    images.forEach(image => {
      if (image.previewUrl) {
        next[image.imageRef] = image.previewUrl
        previewToStable.current[image.previewUrl] = image.imageRef
      }
    })
    if (Object.keys(next).length) {
      setPreviewByStable(previous =>
        Object.keys(next).every(url => previous[url] === next[url])
          ? previous
          : { ...previous, ...next }
      )
    }
    return next
  }

  useEffect(() => {
    if (!id) return
    let active = true
    const postId = Number(id)
    if (!Number.isInteger(postId) || postId <= 0) {
      error(t('errors.detail'))
      history.replace(NonAuthRoutes.AtlasBlog)
      return
    }
    setLoading(true)
    getPost(postId)
      .then(async post => {
        if (!active) return
        setForm({
          titulo: post.titulo || '',
          idioma: post.idioma,
          resumen: post.resumen || '',
          autor: post.autor || '',
          urlImagen: post.urlImagen || ''
        })
        setPostStatus(post.estado)
        setPublishedAt(post.fechaPublicacion)
        let replacements: Record<string, string> = {}
        try {
          const images = await findPreviews([
            ...imageUrlsInHtml(post.contenido || ''),
            ...(post.urlImagen ? [post.urlImagen] : [])
          ])
          if (!active) return
          replacements = rememberImages(images)
        } catch (_) {
          if (active) error(t('errors.images'))
        }
        if (active) {
          setInitialHtml(mapImageSources(post.contenido || '', replacements))
          setReady(true)
        }
      })
      .catch(() => {
        if (active) {
          error(t('errors.detail'))
          history.replace(NonAuthRoutes.AtlasBlog)
        }
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
  }, [id])

  const insertMarkdown = (snippet: string, start: number, end: number) => {
    pendingMarkdownCursor.current = start + snippet.length
    setMarkdownText(previous => previous.slice(0, start) + snippet + previous.slice(end))
    setMarkdownChanged(true)
  }

  const toggleBlogView = () => {
    const quill = quillRef.current
    if (!quill) return
    if (blogView === 'rich') {
      const stableHtml = mapImageSources(quill.root.innerHTML, previewToStable.current)
      setMarkdownText(htmlToBlogMarkdown(stableHtml))
      setMarkdownChanged(false)
      setBlogView('markdown')
    } else {
      if (markdownChanged) {
        const html = markdownToBlogHtml(markdownText)
        quill.clipboard.dangerouslyPasteHTML(mapImageSources(html, previewByStable))
      }
      setBlogView('rich')
    }
  }

  const openLinkDialog = () => {
    if (blogView === 'markdown') {
      const input = markdownInputRef.current
      const start = input?.selectionStart ?? markdownText.length
      const end = input?.selectionEnd ?? start
      setLinkSelection({ view: 'markdown', start, end, label: markdownText.slice(start, end) })
    } else {
      const quill = quillRef.current
      if (!quill) return
      const range = quill.getSelection() || richSelectionRef.current || { index: quill.getLength() - 1, length: 0 }
      setLinkSelection({ view: 'rich', index: range.index, length: range.length })
    }
    setLinkUrl('')
    setLinkError(false)
  }

  const confirmLink = () => {
    if (!linkSelection) return
    const url = normalizeBlogLink(linkUrl)
    if (!url) {
      setLinkError(true)
      return
    }
    if (linkSelection.view === 'markdown') {
      insertMarkdown(blogMarkdownLink(linkSelection.label, url), linkSelection.start, linkSelection.end)
    } else {
      const quill = quillRef.current
      if (!quill) return
      if (linkSelection.length) {
        quill.formatText(linkSelection.index, linkSelection.length, 'link', url, 'user')
        quill.setSelection(linkSelection.index + linkSelection.length, 0, 'user')
      } else {
        quill.insertText(linkSelection.index, url, 'link', url, 'user')
        quill.setSelection(linkSelection.index + url.length, 0, 'user')
      }
    }
    setLinkSelection(null)
  }

  const insertImage = (image: AtlasPostImage) => {
    rememberImages([image])
    if (blogView === 'markdown') {
      const start = markdownInputRef.current?.selectionStart ?? markdownText.length
      const end = markdownInputRef.current?.selectionEnd ?? start
      insertMarkdown(blogMarkdownImage(image.fileName, image.imageRef), start, end)
      return
    }
    const quill = quillRef.current
    if (!quill) return
    const index = quill.getSelection(true)?.index ?? quill.getLength() - 1
    if (!image.previewUrl) {
      error(t('errors.images'))
      return
    }
    quill.insertEmbed(index, 'image', image.previewUrl, 'user')
    quill.setSelection(index + 1, 0)
  }

  const buildPayload = (): AtlasPostWrite | null => {
    const titulo = form.titulo.trim()
    if (!titulo || titulo.length > 200 || form.resumen.trim().length > 500) {
      error(t('errors.validation'))
      return null
    }
    const html = blogView === 'markdown' && markdownChanged
      ? markdownToBlogHtml(markdownText)
      : quillRef.current?.root.innerHTML || ''
    const stableHtml = cleanBlogHtml(mapImageSources(html, previewToStable.current))
    const imageRefs = imageUrlsInHtml(stableHtml)
    if (imageRefs.some(ref => !/^[a-zA-Z0-9][a-zA-Z0-9._-]*\.(jpe?g|png|gif|webp)$/i.test(ref)) ||
      (form.urlImagen && !/^[a-zA-Z0-9][a-zA-Z0-9._-]*\.(jpe?g|png|gif|webp)$/i.test(form.urlImagen))) {
      error(t('errors.imageReference'))
      return null
    }
    return {
      titulo,
      idioma: form.idioma,
      resumen: form.resumen.trim() || null,
      contenido: stableHtml.replace(/^(<p><br><\/p>\s*)+$/, '') || null,
      urlImagen: form.urlImagen.trim() || null,
      autor: form.autor.trim() || null
    }
  }

  const canPublish = (payload: AtlasPostWrite): boolean => {
    const content = document.createElement('div')
    content.innerHTML = payload.contenido || ''
    if (!payload.resumen || !payload.autor || (!content.textContent?.trim() && !content.querySelector('img'))) {
      error(t('errors.incomplete'))
      return false
    }
    return true
  }

  const publicationDate = (): Date | null => {
    if (publicationMode === 'now') return new Date()
    const date = new Date(scheduledAt)
    if (!scheduledAt || isNaN(date.getTime()) || date.getTime() <= Date.now()) {
      error(t('errors.futureDate'))
      return null
    }
    return date
  }

  const handleSave = async () => {
    if (saving) return
    const payload = buildPayload()
    if (!payload) return
    setSaving(true)
    try {
      if (isNew) await createPost(payload)
      else await replacePost(Number(id), payload)
      info(t('messages.saved'))
      history.push(NonAuthRoutes.AtlasBlog)
    } catch (_) {
      error(t('errors.save'))
      setSaving(false)
    }
  }

  const openPublishConfirmation = () => {
    if (saving) return
    const payload = buildPayload()
    if (!payload || !canPublish(payload) || !publicationDate()) return
    setPublishConfirmation(true)
  }

  const confirmPublish = async () => {
    if (saving) return
    const payload = buildPayload()
    if (!payload || !canPublish(payload)) return
    const date = publicationDate()
    if (!date) {
      setPublishConfirmation(false)
      return
    }
    setSaving(true)
    let postId = Number(id)
    try {
      if (isNew) {
        const created = await createPost(payload)
        postId = created.id
      } else {
        await replacePost(postId, payload)
      }
    } catch (_) {
      error(t('errors.save'))
      setSaving(false)
      return
    }
    try {
      await publishPost(postId, date.toISOString())
      info(t(publicationMode === 'now' ? 'messages.published' : 'messages.scheduled'))
      history.push(NonAuthRoutes.AtlasBlog)
    } catch (_) {
      error(t('errors.publishAfterSave'))
      if (isNew) history.replace(`/atlas/blog/${postId}`)
      else {
        setPublishConfirmation(false)
        setSaving(false)
      }
    }
  }

  const refreshPreview = async (event: React.SyntheticEvent<HTMLDivElement>) => {
    const target = event.target as HTMLImageElement
    if (target.tagName !== 'IMG') return
    const stable = previewToStable.current[target.src]
    if (!stable) return
    try {
      const found = await findPreviews([stable])
      const fresh = found[0]
      if (fresh?.previewUrl && fresh.previewUrl !== target.src) {
        rememberImages([fresh])
        target.src = fresh.previewUrl
      }
    } catch (_) {
      /* The article remains editable if a thumbnail cannot refresh. */
    }
  }

  return (
    <Page layout={PageLayout.SINGLE_COLUMN} fullWidth={true}>
      <PageCenterColumn>
        <Box mx={2}>
          <PageHeader title={isNew ? t('editor.addTitle') : t('editor.editTitle')}>
            {ready && !loading && (
              <>
                <Button
                  variant="text"
                  color="primary"
                  size="small"
                  disabled={saving}
                  onClick={() => history.push(NonAuthRoutes.AtlasBlog)}
                >
                  {t('cancel')}
                </Button>
                <Button
                  variant={postStatus === 'Publicado' ? 'contained' : 'text'}
                  color="primary"
                  size="small"
                  disabled={saving}
                  onClick={handleSave}
                >
                  {t('save')}
                </Button>
                {postStatus !== 'Publicado' && (
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    disabled={saving}
                    onClick={openPublishConfirmation}
                  >
                    {t(publicationMode === 'now' ? 'editor.publishNow' : 'editor.schedule')}
                  </Button>
                )}
              </>
            )}
          </PageHeader>
        </Box>
        {loading || !ready ? (
          <CircularProgress />
        ) : (
          <StandardCard variant="outlined" title={t('editor.details')}>
            {postStatus === 'Publicado' && (
              <Box mb={2}>
                <Typography color="error">{t('editor.publishedNotice')}</Typography>
              </Box>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12} md={postStatus ? 8 : 10}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label={t('columns.title')}
                  required
                  value={form.titulo}
                  inputProps={{ maxLength: 200 }}
                  onChange={event => {
                    const value = event.target.value
                    setForm(previous => ({ ...previous, titulo: value }))
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={postStatus ? 6 : 12} md={2}>
                <TextField
                  select
                  fullWidth
                  variant="outlined"
                  required
                  label={t('columns.language')}
                  value={form.idioma}
                  onChange={event => {
                    const value = event.target.value as AtlasLanguage
                    setForm(previous => ({ ...previous, idioma: value }))
                  }}
                >
                  <MenuItem value="ES">ES</MenuItem>
                  <MenuItem value="EN">EN</MenuItem>
                </TextField>
              </Grid>
              {postStatus && (
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label={t('columns.status')}
                    value={t(`statuses.${postStatus}`)}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant="outlined"
                  multiline
                  rows={3}
                  label={t('editor.summary')}
                  value={form.resumen}
                  inputProps={{ maxLength: 500 }}
                  helperText={`${form.resumen.length}/500`}
                  onChange={event => {
                    const value = event.target.value
                    setForm(previous => ({ ...previous, resumen: value }))
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label={t('columns.author')}
                  value={form.autor}
                  onChange={event => {
                    const value = event.target.value
                    setForm(previous => ({ ...previous, autor: value }))
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography>{t('editor.cover')}: {form.urlImagen || t('editor.noCover')}</Typography>
                {form.urlImagen && <Button onClick={() => setForm(previous => ({ ...previous, urlImagen: '' }))}>
                  {t('editor.removeCover')}
                </Button>}
              </Grid>
              <Grid item xs={12}>
                {postStatus === 'Publicado' ? (
                  <TextField
                    fullWidth
                    id="atlas-blog-published-at"
                    variant="outlined"
                    label={t('publicationDate')}
                    value={publishedAt && !isNaN(new Date(publishedAt).getTime())
                      ? new Date(publishedAt).toLocaleString()
                      : ''}
                    InputProps={{ readOnly: true }}
                  />
                ) : (
                  <>
                    <RadioGroup
                      row
                      value={publicationMode}
                      onChange={event => setPublicationMode(event.target.value as PublicationMode)}
                    >
                      <FormControlLabel value="now" control={<Radio color="primary" />} label={t('editor.publishNow')} />
                      <FormControlLabel value="scheduled" control={<Radio color="primary" />} label={t('editor.schedule')} />
                    </RadioGroup>
                    {publicationMode === 'scheduled' && (
                      <TextField
                        id="atlas-blog-scheduled-at"
                        type="datetime-local"
                        variant="outlined"
                        label={t('publicationDate')}
                        value={scheduledAt}
                        InputLabelProps={{ shrink: true }}
                        inputProps={{ min: localDateTime(new Date()) }}
                        onChange={event => setScheduledAt(event.target.value)}
                      />
                    )}
                    <Typography variant="body2" color="textSecondary">{t('editor.publicationHelp')}</Typography>
                  </>
                )}
              </Grid>
            </Grid>
            {form.urlImagen && previewByStable[form.urlImagen] && (
              <Box mt={1}>
                <img
                  src={previewByStable[form.urlImagen]}
                  alt={t('editor.cover')}
                  style={{ maxWidth: 240, maxHeight: 150 }}
                />
              </Box>
            )}
            <Box mt={3} mb={1}>
              <Typography variant="h6">{t('editor.content')}</Typography>
            </Box>
            <div onErrorCapture={refreshPreview}>
              <RichTextEditor
                ref={quillRef}
                blogMode
                initialHtml={initialHtml}
                height="min(700px, 75vh)"
                blogView={blogView}
                blogLabels={{
                  markdown: t('editor.markdownView'),
                  rich: t('editor.richView'),
                  link: t('editor.insertLink'),
                  content: t('editor.markdownContent')
                }}
                onToggleBlogView={toggleBlogView}
                onBlogLinkClick={openLinkDialog}
                onSelectionChange={range => {
                  if (range) richSelectionRef.current = range
                }}
                markdownValue={markdownText}
                onMarkdownChange={value => {
                  setMarkdownText(value)
                  setMarkdownChanged(true)
                }}
                markdownInputRef={markdownInputRef}
              />
            </div>
            <AtlasBlogImages
              onUseCover={imageRef => setForm(previous => ({ ...previous, urlImagen: imageRef }))}
              onInsert={insertImage}
              onImagesLoaded={rememberImages}
            />
          </StandardCard>
        )}
      </PageCenterColumn>
      <Dialog open={!!linkSelection} onClose={() => setLinkSelection(null)} fullWidth maxWidth="sm">
        <DialogTitle>{t('editor.insertLink')}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            id="atlas-blog-link-url"
            variant="outlined"
            margin="dense"
            label={t('editor.linkUrl')}
            value={linkUrl}
            error={linkError}
            helperText={linkError ? t('errors.linkUrl') : undefined}
            onChange={event => {
              setLinkUrl(event.target.value)
              setLinkError(false)
            }}
            onKeyDown={event => {
              if (event.key === 'Enter') confirmLink()
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkSelection(null)}>{t('cancel')}</Button>
          <Button color="primary" onClick={confirmLink}>{t('editor.insertLink')}</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={publishConfirmation}
        onClose={() => !saving && setPublishConfirmation(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>{t(publicationMode === 'now' ? 'editor.publishNow' : 'editor.schedule')}</DialogTitle>
        <DialogContent>
          <Typography>{t('confirm.publish', { title: form.titulo.trim() })}</Typography>
          <Typography variant="body2">
            {t('confirm.visibleFrom', {
              date: publicationMode === 'now' ? new Date().toLocaleString() : new Date(scheduledAt).toLocaleString()
            })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button disabled={saving} onClick={() => setPublishConfirmation(false)}>{t('cancel')}</Button>
          <Button disabled={saving} color="primary" onClick={confirmPublish}>
            {t('confirm.button')}
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  )
}
